import { INPUT_PARTS } from './inputParts';
import type { InputSize, InputStyleConfig } from './types';

type SizingData<Size extends InputSize> = {
  fontSize: Size;
  lineHeight: Size;
  px: number;
  h: number;
};

export const INPUT_SIZE_TO_SIZING_DATA_MAP = {
  lg: {
    fontSize: 'lg',
    lineHeight: 'lg',
    px: 1.5,
    h: 5,
  },
  md: {
    fontSize: 'md',
    lineHeight: 'md',
    px: 1,
    h: 4,
  },
  sm: {
    fontSize: 'sm',
    lineHeight: 'sm',
    px: 0.75,
    h: 3,
  },
} as const satisfies Readonly<{ [Size in InputSize]: SizingData<Size> }>;

const baseStyle: InputStyleConfig['baseStyle'] = () => ({
  field: {
    bg: 'page.surface',
    border: '1px solid',
    borderColor: 'separator.border',
    borderRadius: 'sm',
    color: 'feature.primary',
    fontFamily: 'default',
    fontWeight: 'default',
    minWidth: 0,
    outline: 0,
    position: 'relative',
    top: 0,
    transitionProperty: 'common',
    transitionDuration: 'normal',
    width: '100%',

    _placeholder: {
      color: 'feature.placeholder',
    },

    _hover: {
      borderColor: 'interaction.hover',
    },

    _invalid: {
      borderColor: 'alert.error',
    },
    _focus: {
      borderColor: 'interaction.main',
      boxShadow: 'focus',
    },
    _readOnly: {
      boxShadow: 'none',
      borderColor: 'separator.border',
      cursor: 'text',
      userSelect: 'all',
    },
    _disabled: {
      bg: 'interaction.surfaceDisabled',
      borderColor: 'separator.border',
      color: 'feature.secondary',
      WebkitTextFillColor: `feature.secondary`,
      cursor: 'not-allowed',
      _placeholder: {
        color: 'feature.placeholder',
        WebkitTextFillColor: `feature.placeholder`,
      },
    },
  },
  addon: {
    border: '1px solid',
    borderColor: 'separator.border',
    bg: 'page.plateLight',
    color: 'feature.secondary',
    fontFamily: 'default',
    fontWeight: 'default',
  },
});

const SIZES = {
  lg: {
    field: INPUT_SIZE_TO_SIZING_DATA_MAP.lg,
    addon: INPUT_SIZE_TO_SIZING_DATA_MAP.lg,
    element: {
      fontSize: '1.5rem',
    },
  },
  md: {
    field: INPUT_SIZE_TO_SIZING_DATA_MAP.md,
    addon: INPUT_SIZE_TO_SIZING_DATA_MAP.md,
    element: {
      fontSize: '1.25rem',
    },
  },
  sm: {
    field: INPUT_SIZE_TO_SIZING_DATA_MAP.sm,
    addon: INPUT_SIZE_TO_SIZING_DATA_MAP.sm,
    element: {
      fontSize: '1rem',
    },
  },
} as const;

const DEFAULT_PROPS = {
  size: 'md',
} as const;

export const INPUT_STYLE_CONFIG: InputStyleConfig = {
  parts: INPUT_PARTS,
  baseStyle,
  sizes: SIZES,
  defaultProps: DEFAULT_PROPS,
};
